import { Rule } from 'rc-field-form/es/interface';
import { CreateRegistrationFields, CreateScheduleFields } from 'src/app/modules/Recruitment/types/license-exam-types';
import {
  endTimeValidator,
  lastRegistrationValidator,
  quotaValidator,
  minimumQuotaValidator,
  startTimeValidator,
  examDateValidator,
  resultValidator,
  scoreValidator,
  startRegistrationValidator,
} from './form.util';
import { Translation, TranslationWithParams } from 'src/app/i18n';
import { getLastRegistrationDateLimitDays } from './configs.util';
import { Results } from '../results/results-template';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { getConfigurations } from 'src/app/common/utils';

export const createScheduleConfigs = (region: string, options?: { emptyMsgKey?: string }) => {
  const config = getConfigurations();
  const isPHKL = get(config, 'Global.region.lbuCode') === 'PHKL';

  const placeEnter = Translation('app.input.placeholder.please-enter');
  const getEmptyMsg = (label: string) =>
    TranslationWithParams(options?.emptyMsgKey ?? 'component.form-required-text', { field: label });

  return [
    {
      name: CreateScheduleFields.examCode,
      label: Translation('recruitment.exam.examCode'),
      rules: [{ required: true, message: getEmptyMsg(Translation('recruitment.exam.examCode')) }],
      placeholder: Translation('recruitment.exam.schedule.examCode_placeholder'),
    },
    {
      name: CreateScheduleFields.examNameEnglish,
      label: Translation('recruitment.exam.examName'),
      rules: [{ required: true, message: getEmptyMsg(Translation('recruitment.exam.examName')) }],
      placeholder: placeEnter,
    },
    {
      name: CreateScheduleFields.examNameLocal,
      label: Translation('recruitment.exam.examNameLocal'),
      rules: [{ required: true, message: getEmptyMsg(Translation('recruitment.exam.examNameLocal')) }],
      placeholder: placeEnter,
    },
    {
      name: CreateScheduleFields.sessionId,
      label: Translation('recruitment.exam.sessionId'),
      rules: [{ required: true, message: getEmptyMsg(Translation('recruitment.exam.sessionId')) }],
      placeholder: placeEnter,
    },
    {
      name: CreateScheduleFields.examType,
      label: Translation('recruitment.exam.examType'),
      rules: [{ required: true, message: getEmptyMsg(Translation('recruitment.exam.examType')) }],
    },
    {
      name: CreateScheduleFields.examMode,
      label: Translation('recruitment.exam.examMode'),
      rules: [{ required: true, message: getEmptyMsg(Translation('recruitment.exam.examMode')) }],
    },
    {
      name: CreateScheduleFields.examDate,
      label: Translation('recruitment.exam.examDate'),
      rules: [
        {
          required: true,
          validator(rule, value, callback) {
            const invalidMsg = TranslationWithParams('recruitment.exam.schedule.date_time_do_not_follow_validator', {
              field: Translation('recruitment.exam.examDate'),
            });

            const errorMsg = TranslationWithParams('recruitment.exam.schedule.exam_date_future_validator', {
              field: Translation('recruitment.exam.examDate'),
            });
            const msg = examDateValidator(
              {
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.examDate')),
                rule,
                invalidMsg,
                errorMsg,
              },
              value,
            );
            callback(msg);
          },
        },
      ],
    },
    {
      name: CreateScheduleFields.startTime,
      label: Translation('recruitment.exam.examDateStartTime'),
      rules: [
        {
          required: true,
          validator(rule, value, callback) {
            const invalidMsg = TranslationWithParams('recruitment.exam.schedule.time_validator', {
              field: Translation('recruitment.exam.examDateStartTime'),
            });
            const msg = startTimeValidator(
              {
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.examDateStartTime')),
                rule,
                invalidMsg,
              },
              value,
            );
            callback(msg);
          },
        },
      ],
    },
    {
      name: CreateScheduleFields.endTime,
      label: Translation('recruitment.exam.examDateEndTime'),
      rules: [
        (form: any) => ({
          required: true,
          validator(rule, value, callback) {
            const startTime = form.getFieldValue(CreateScheduleFields.startTime);
            const msg = endTimeValidator(
              {
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.examDateEndTime')),
                errorMsg: Translation('recruitment.exam.schedule.endTime_validator'),
                rule,
              },
              startTime,
              value,
            );
            callback(msg);
          },
        }),
      ],
    },
    {
      name: CreateScheduleFields.examCentre,
      label: Translation('recruitment.exam.venue'),
      rules: [{ required: true, message: getEmptyMsg(Translation('recruitment.exam.venue')) }],
      placeholder: placeEnter,
    },
    {
      name: CreateScheduleFields.address,
      label: Translation('recruitment.exam.address'),
      rules: [{ required: true, message: getEmptyMsg(Translation('recruitment.exam.address')) }],
      placeholder: placeEnter,
    },
    {
      name: CreateScheduleFields.startRegistrationDate,
      label: Translation('recruitment.exam.start_register_time'),
      rules: [
        (form: any) => ({
          required: true,
          validator(rule, value, callback) {
            const lastRegistrationDate = form.getFieldValue(CreateScheduleFields.lastRegistrationDate);
            const msg = startRegistrationValidator(
              {
                rule,
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.start_register_time')),
                errorMsg: Translation('recruitment.exam.schedule.start_register_time_validator'),
                invalidMsg: TranslationWithParams('recruitment.exam.schedule.time_validator', {
                  field: Translation('recruitment.exam.start_register_time'),
                }),
              },
              value,
              lastRegistrationDate,
            );
            callback(msg);
          },
        }),
      ],
    },
    {
      name: CreateScheduleFields.lastRegistrationDate,
      label: Translation('recruitment.exam.last_register_time'),
      rules: [
        (form: any) => ({
          required: true,
          validator(rule, value, callback) {
            const examDate = form.getFieldValue(CreateScheduleFields.examDate);
            const startRegistrationDate = form.getFieldValue(CreateScheduleFields.startRegistrationDate);
            const day = getLastRegistrationDateLimitDays();
            const errorMsg = TranslationWithParams('recruitment.exam.schedule.last_register_time_validator', { day });
            const invalidMsg = TranslationWithParams('recruitment.exam.schedule.time_validator', {
              field: Translation('recruitment.exam.last_register_time'),
            });
            const msg = lastRegistrationValidator(
              {
                rule,
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.last_register_time')),
                errorMsg,
                day,
                invalidMsg,
                futureDayErrorMsg: TranslationWithParams('recruitment.exam.schedule.exam_date_future_validator', {
                  field: Translation('recruitment.exam.last_register_time'),
                }),
                startRegistrationDate,
              },
              examDate,
              value,
            );
            callback(msg);
          },
        }),
      ],
    },
    {
      name: CreateScheduleFields.quota,
      label: Translation('recruitment.exam.examQuota'),
      rules: [
        {
          required: true,
          validator(rule, value, callback) {
            const msg = quotaValidator(
              {
                rule,
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.examQuota')),
                errorMsg: Translation('recruitment.exam.schedule.quota_validator'),
              },
              value,
            );

            callback(msg);
          },
        },
      ],
      placeholder: placeEnter,
    },
    {
      name: CreateScheduleFields.remainingQuota,
      label: Translation('recruitment.exam.minimum_quota'),
      rules: [
        (form: any) => ({
          required: false,
          validator(rule, value, callback) {
            const quota = form.getFieldValue(CreateScheduleFields.quota);
            const msg = minimumQuotaValidator(
              {
                rule,
                errorMsg: Translation('recruitment.exam.min_quota_quota_validator'),
                integerError: Translation('recruitment.exam.min_quota_quota_validator'),
              },
              quota,
              value,
            );

            callback(msg);
          },
        }),
      ],
      placeholder: placeEnter,
    },
    {
      name: CreateScheduleFields.remark,
      label: Translation('recruitment.exam.remark'),
      rules: [{ required: false }],
      placeholder: placeEnter,
    },
  ] as {
    name: CreateScheduleFields;
    label: string;
    rules: Rule[];
    placeholder?: string;
  }[];
};

export const createResultConfigs = (region: string, options?: { emptyMsgKey?: string }) => {
  const isMandatoryFields = (field: string) =>
    ['firstName', 'lastName', 'examCode', 'examDate', 'startTime', 'endTime', 'examCentre', 'result', 'score'].includes(
      field,
    );

  const scheduleConfigs = createScheduleConfigs(region, options)
    .filter((i) =>
      [
        'examCode',
        'examType',
        'examNameLocal',
        'examNameEnglish',
        // 'examDate',
        'examCentre',
        'startTime',
        'endTime',
        'address',
      ].includes(i.name),
    )
    .map(({ rules, ...rest }) => {
      return {
        ...rest,
        rules: rules.map((r) => {
          if (typeof r === 'function') {
            return (form: any) => ({ ...r(form), required: isMandatoryFields(rest.name) });
          } else {
            return { ...r, required: isMandatoryFields(rest.name) };
          }
        }),
      };
    }); //
  const getEmptyMsg = (label: string) =>
    TranslationWithParams(options?.emptyMsgKey ?? 'component.form-required-text', { field: label });

  return [
    ...scheduleConfigs,
    {
      name: CreateRegistrationFields.examDate,
      label: Translation('recruitment.exam.examDate'),
      rules: [
        {
          required: isMandatoryFields(CreateRegistrationFields.examDate),
          validator(rule, value, callback) {
            const invalidMsg = TranslationWithParams('recruitment.exam.schedule.date_time_do_not_follow_validator', {
              field: Translation('recruitment.exam.examDate'),
            });

            const errorMsg = TranslationWithParams('recruitment.exam.schedule.exam_date_passed_validator', {
              field: Translation('recruitment.exam.examDate'),
            });
            const msg = examDateValidator(
              {
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.examDate')),
                rule,
                invalidMsg,
                errorMsg,
              },
              value,
              dayjs(),
              (examDate: any, dayNow: any) => dayjs(examDate).isAfter(dayNow),
            );
            callback(msg);
          },
        },
      ],
    },
    {
      name: CreateRegistrationFields.registrationId,
      label: Translation('recruitment.exam.registration.id'),
      rules: [
        {
          required: isMandatoryFields('registrationId'),
          message: getEmptyMsg(Translation('recruitment.exam.registration.id')),
        },
      ],
    },
    {
      name: CreateRegistrationFields.applicantId,
      label: Translation('recruitment.exam.registration.applicantId'),
      rules: [
        {
          required: isMandatoryFields('applicantId'),
          message: getEmptyMsg(Translation('recruitment.exam.registration.applicantId')),
        },
      ],
    },
    {
      name: CreateRegistrationFields.firstname,
      label: Translation('recruitment.exam.registration.firstName'),
      rules: [
        {
          required: isMandatoryFields('firstName'),
          message: getEmptyMsg(Translation('recruitment.exam.registration.firstName')),
        },
      ],
    },
    {
      name: CreateRegistrationFields.lastname,
      label: Translation('recruitment.exam.registration.lastName'),
      rules: [
        {
          required: isMandatoryFields('lastName'),
          message: getEmptyMsg(Translation('recruitment.exam.registration.lastName')),
        },
      ],
    },
    {
      name: CreateRegistrationFields.result,
      label: Translation('recruitment.exam.registration.result'),
      rules: [
        {
          required: isMandatoryFields('result'),
          validator(rule, value, callback) {
            const msg = resultValidator(
              {
                rule,
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.registration.result')),
                errorMsg: TranslationWithParams('recruitment.exam.result_value_validator', {
                  text: [Results.PASSED, Results.FAILED, Results.ABSENT, Results.DQ /*PHKL custom*/, Results.PENDING /*PHKL custom*/,].join('/'),
                }),
              },
              value,
            );

            callback(msg);
          },
        },
      ],
    },
    {
      name: CreateRegistrationFields.score,
      label: Translation('recruitment.exam.registration.score'),
      rules: [
        {
          required: isMandatoryFields('score'),
          validator(rule, { value, result }, callback) {
            const msg = scoreValidator(
              {
                rule,
                emptyMsg: getEmptyMsg(Translation('recruitment.exam.registration.score')),
                errorMsg: Translation('recruitment.exam.score_value_validator'),
              },
              value,
              result,
            );

            callback(msg);
          },
        },
      ],
    },
  ] as {
    name: CreateRegistrationFields;
    label: string;
    rules: Rule[];
    placeholder?: string;
  }[];
};
